import { useState, useRef } from "react";
import { Upload, X } from "lucide-react";
import api, { imgUrl, formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";

export function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 border border-black/10 cursor-pointer bg-white" data-testid={`${testid}-picker`} />
      <div className="flex-1">
        <Label className="text-xs uppercase tracking-widest">{label}</Label>
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-8 text-sm" data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}

export function ImageField({ label, value, onChange, testid, hint }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <div className="flex items-center gap-3 mt-2">
        <div className="w-24 h-16 border border-black/10 bg-muted flex items-center justify-center overflow-hidden">
          {value ? <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-muted-foreground">Default</span>}
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300 flex items-center gap-2"
          data-testid={`${testid}-upload`}>
          <Upload strokeWidth={1.5} size={14} /> {uploading ? "Uploading..." : "Upload"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="p-2 hover:text-destructive transition-colors duration-300" data-testid={`${testid}-clear`} aria-label="Remove">
            <X strokeWidth={1.5} size={16} />
          </button>
        )}
      </div>
      {hint && <p className="text-[11px] text-muted-foreground mt-2">{hint}</p>}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid={`${testid}-file`} />
    </div>
  );
}

export function TextField({ label, value, onChange, testid, textarea, rows = 3, hint }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
          className="mt-2 w-full border border-input bg-white px-3 py-2 text-sm outline-none focus:border-gold transition-colors duration-300 resize-y"
          data-testid={testid} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-2" data-testid={testid} />
      )}
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
